import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  AddUserWithPlacement,
  CollectFeedback,
  CreateSchedule,
  CreateWeeklySchedule,
  GenerateTracksWithOptions,
  GetAIStatus,
  GetLearnersForSchedule,
  GetScheduleByIdSession,
  GetSchedulesByLearning,
  GetSchedulesExportData,
  GetSingleSchedules,
  GetWeeklySchedules,
  SetError,
  SetLoading,
  TransferLearner,
  ValidateSchedule,
} from '@tsin-core/actions/schedule.action';
import {
  LoadingScheduleState,
  ScheduleStateModel,
  TransferLearnerResult,
  WeeklyScheduleLearnerModel,
  WeeklyScheduleModel,
} from '@tsin-core/models/schedule.model';
import { ScheduleService } from '@tsin-core/services/http/schedule.service';
import { GetAttendanceByWeeklySchedule } from '@tsin-core/actions/attendance.action';
import { catchError, of, tap, throwError } from 'rxjs';

@State<ScheduleStateModel>({
  name: 'scheduleState',
  defaults: {
    loading: LoadingScheduleState.loadingList,
    schedule: [],
    weeklySchedules: [],
    scheduleExports: [],
    error: null,
    validationReport: null,
    aiStatus: null,
    lastTransferResult: null,
    scheduleLearners: [],
  },
})
@Injectable()
export class ScheduleState {
  scheduleService = inject(ScheduleService);

  @Selector()
  static getValidationReport(state: ScheduleStateModel) {
    return state.validationReport;
  }

  @Selector()
  static getAIStatus(state: ScheduleStateModel) {
    return state.aiStatus;
  }

  
  static getTrackManagement(state: ScheduleStateModel) {
    return state.schedule;
  }

  @Selector()
  static getWeeklySchedules(state: ScheduleStateModel) {
    return state.weeklySchedules;
  }

  @Selector()
  static getLoading(state: ScheduleStateModel) {
    return state.loading;
  }

  @Selector()
  static getError(state: ScheduleStateModel) {
    return state.error;
  }

  @Action(GetSchedulesByLearning)
  getSchedule(ctx: StateContext<ScheduleStateModel>, action: GetSchedulesByLearning) {
    ctx.patchState({ loading: LoadingScheduleState.loadingList });

    return this.scheduleService.getSchedule().pipe(
      tap((tracks: any) => {
        ctx.patchState({
          schedule: tracks.data,
          loading: LoadingScheduleState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingScheduleState.notLoading,
          error: error.message,
        });
        return of(error);
      })
    );
  }


  @Action(GetSchedulesExportData)
  getSimulationAllocationExportData(ctx: StateContext<ScheduleStateModel>, action: GetSchedulesExportData) {
    ctx.patchState({ loading: LoadingScheduleState.loadingList });

    return this.scheduleService.getSimulationAllocationExportData(action.payload).pipe(
      tap((allocations: any) => {
        console.log('RESPOSNE:: ', allocations);
        ctx.patchState({
          scheduleExports: allocations,
          loading: LoadingScheduleState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingScheduleState.notLoading,
          error: error.message,
        });
        return of(error);
      })
    );
  }


  @Action(CreateWeeklySchedule)
  createWeeklySchedule(
    ctx: StateContext<ScheduleStateModel>,
    action: CreateWeeklySchedule
  ) {
    ctx.patchState({ loading: LoadingScheduleState.loadingAddUpdate });

    return this.scheduleService.createWeeklySchedule(action.payload).pipe(
      tap((result: any) => {
        const state = ctx.getState();
        const weeklySchedulesArray = [...state.weeklySchedules, result.data].reverse();
        ctx.patchState({
          weeklySchedules: weeklySchedulesArray,
          loading: LoadingScheduleState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingScheduleState.notLoading,
          error: error.message,
        });
        throw error;
      })
    );
  }

  @Action(GetWeeklySchedules)
  getWeeklySchedule(ctx: StateContext<ScheduleStateModel>, action: GetWeeklySchedules) {
    ctx.patchState({ loading: LoadingScheduleState.loadingList });
    return this.scheduleService.getWeeklySchedule().pipe(
      tap((schedules: WeeklyScheduleModel[]) => {
        ctx.patchState({
          weeklySchedules: schedules.reverse(),
          loading: LoadingScheduleState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingScheduleState.notLoading,
          error: error.message,
        });
        throw error;
      })
    );
  }

  @Action(CreateSchedule)
  createSchedule(
    ctx: StateContext<ScheduleStateModel>,
    action: CreateSchedule
  ) {
    ctx.patchState({ loading: LoadingScheduleState.loadingAddUpdate });

    return this.scheduleService.createSchedule(action.payload).pipe(
      tap((result: any) => {
        const state = ctx.getState();
        ctx.patchState({
          schedule: [...state.schedule, result],
          loading: LoadingScheduleState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingScheduleState.notLoading,
          error: error.error.message,
        });
        throw error;
      })
    );
  }

  @Action(GetScheduleByIdSession)
  getScheduleByIdSession(ctx: StateContext<ScheduleStateModel>, action: GetScheduleByIdSession) {
    ctx.patchState({ loading: LoadingScheduleState.loadingList });

    return this.scheduleService.getScheduleByIdSession( action.payload ).pipe(
      tap((results: any) => {
        ctx.patchState({
          schedule:  results,
          loading: LoadingScheduleState.notLoading
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingScheduleState.notLoading,
          error: error.message,
        });
        throw error;
      })
    );
  }

  @Action(SetLoading)
  setLoading(ctx: StateContext<ScheduleStateModel>, action: SetLoading) {
    ctx.patchState({ loading: action.loading });
  }

  @Action(SetError)
  setError(ctx: StateContext<ScheduleStateModel>, action: SetError) {
    ctx.patchState({ error: action.error });
  }


  // ============================================================================
  // NEW ACTION HANDLERS FOR ALGORITHM UPDATE & AI/ML
  // ============================================================================

  @Action(GenerateTracksWithOptions)
  generateTracksWithOptions(ctx: StateContext<ScheduleStateModel>, action: GenerateTracksWithOptions) {
    ctx.patchState({ loading: LoadingScheduleState.loadingAddUpdate });

    return this.scheduleService.generateTracksWithOptions(action.payload).pipe(
      tap((result: any) => {
        ctx.patchState({
          loading: LoadingScheduleState.notLoading,
          validationReport: result.validationReport || null,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingScheduleState.notLoading,
          error: error.error?.message || error.message,
        });
        return throwError(() => error);
      })
    );
  }

  @Action(ValidateSchedule)
  validateSchedule(ctx: StateContext<ScheduleStateModel>, action: ValidateSchedule) {
    ctx.patchState({ loading: LoadingScheduleState.loadingList });

    return this.scheduleService.validateSchedule(action.weeklyScheduleId).pipe(
      tap((result: any) => {
        ctx.patchState({
          validationReport: result,
          loading: LoadingScheduleState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingScheduleState.notLoading,
          error: error.error?.message || error.message,
        });
        return throwError(() => error);
      })
    );
  }

  @Action(GetAIStatus)
  getAIStatus(ctx: StateContext<ScheduleStateModel>) {
    return this.scheduleService.getAIStatus().pipe(
      tap((result: any) => {
        ctx.patchState({
          aiStatus: result,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          error: error.error?.message || error.message,
        });
        return throwError(() => error);
      })
    );
  }

  @Action(CollectFeedback)
  collectFeedback(ctx: StateContext<ScheduleStateModel>, action: CollectFeedback) {
    return this.scheduleService.collectFeedback(action.weeklyScheduleId).pipe(
      tap(() => {
        // Feedback collected successfully
      }),
      catchError((error) => {
        ctx.patchState({
          error: error.error?.message || error.message,
        });
        return throwError(() => error);
      })
    );
  }

  @Action(AddUserWithPlacement)
  addUserWithPlacement(ctx: StateContext<ScheduleStateModel>, action: AddUserWithPlacement) {
    ctx.patchState({ loading: LoadingScheduleState.loadingAddUpdate });

    return this.scheduleService.addUserToSchedule(action.payload).pipe(
      tap(() => {
        ctx.patchState({ loading: LoadingScheduleState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingScheduleState.notLoading, error: error.error?.message || error.message });
        return throwError(() => error);
      })
    );
  }

  /**
   * Transfer a learner from one weekly schedule to another.
   * On success:
   *  - stores the transfer result in state (for the component to display)
   *  - dispatches GetAttendanceByWeeklySchedule for BOTH the old and new
   *    schedule so the attendance sheet always reflects the current roster
   *
   * Transférer un apprenant vers un autre horaire hebdomadaire.
   * En cas de succès :
   *  - stocke le résultat dans l'état (pour affichage dans le composant)
   *  - rafraîchit la feuille de présence des deux horaires (ancien et nouveau)
   */
  @Action(TransferLearner)
  transferLearner(ctx: StateContext<ScheduleStateModel>, action: TransferLearner) {
    ctx.patchState({ loading: LoadingScheduleState.loadingAddUpdate, lastTransferResult: null });

    return this.scheduleService.transferLearner(action.payload).pipe(
      tap((result: TransferLearnerResult) => {
        ctx.patchState({
          loading: LoadingScheduleState.notLoading,
          lastTransferResult: result,
        });

        // Refresh the attendance state for both the old and new schedule so
        // the learner is immediately removed from the source attendance sheet
        // and visible in the target attendance sheet without a page reload.
        //
        // Rafraîchir l'état de présence pour les deux horaires afin que l'apprenant
        // disparaisse immédiatement de la feuille source et apparaisse dans la cible.
        ctx.dispatch([
          new GetAttendanceByWeeklySchedule(action.payload.fromWeeklyScheduleId),
          new GetAttendanceByWeeklySchedule(action.payload.toWeeklyScheduleId),
        ]);
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingScheduleState.notLoading,
          error: error.error?.message || error.message,
        });
        return throwError(() => error);
      }),
    );
  }

  @Selector()
  static getLastTransferResult(state: ScheduleStateModel): TransferLearnerResult | null {
    return state.lastTransferResult;
  }

  @Selector()
  static getScheduleLearners(state: ScheduleStateModel): WeeklyScheduleLearnerModel[] {
    return state.scheduleLearners;
  }

  /**
   * Fetch all unique learners in a weekly schedule.
   * Clears the list first so stale data from a previous schedule doesn't show while loading.
   *
   * Récupère tous les apprenants d'un horaire hebdomadaire.
   * Vide la liste d'abord pour éviter les données périmées de l'horaire précédent.
   */
  @Action(GetLearnersForSchedule)
  getLearnersForSchedule(ctx: StateContext<ScheduleStateModel>, action: GetLearnersForSchedule) {
    ctx.patchState({ scheduleLearners: [], loading: LoadingScheduleState.loadingList });
    return this.scheduleService.getLearnersForSchedule(action.weeklyScheduleId).pipe(
      tap((res) => {
        ctx.patchState({
          scheduleLearners: res.data,
          loading: LoadingScheduleState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          scheduleLearners: [],
          loading: LoadingScheduleState.notLoading,
          error: error.error?.message || error.message,
        });
        return of([]);
      }),
    );
  }
}